/**
 * Per-PRINCIPAL request-rate ceilings, configurable per plan.
 *
 * ── What this is, and what it deliberately is NOT ───────────────────────
 *
 * A rate limit is a SAFETY VALVE — `docs/signals/2026-06-27_RATE_LIMIT_CAP_TUNING.md`
 * sizes the defaults "to stop a runaway loop, not to price compute". This does not
 * change that. It changes only WHERE the number comes from, so an operator can
 * raise the ceiling for a customer whose legitimate workload outgrew the default,
 * without raising it for everyone.
 *
 * COMPUTE is priced per TENANT (credits, caps, `resource_usage`) because it is
 * money and the tenant pays. REQUESTS stay keyed per PRINCIPAL because the thing
 * a rate limit exists to stop — a client in a loop — is one principal. Only the
 * CEILING varies by plan; the KEYING is untouched. A tenant-wide bucket would make
 * colleagues starve each other, which is a failure mode that does not exist today.
 *
 * ── Why `0` is "unset" here and not "block" ─────────────────────────────
 *
 * The opposite of the `null` ≠ `0` rule that governs budget caps, and deliberately:
 * a cap of 0 means "spend nothing", which is a coherent thing to want. A request
 * ceiling of 0 means "every call 429s", which bricks the app and can only ever be a
 * typo or a half-written plan row. So anything non-positive resolves to `null` and
 * the caller falls back to its env default — this fails OPEN, matching the limiter's
 * own posture (a Redis outage degrades to a local bucket rather than refusing).
 */

/** Tiers whose ceiling a plan may raise. These are the SDK-surface tiers, where the
 *  tenant is already known from the verified token claims — so resolving a plan costs
 *  a cached lookup, not an extra round trip on an unauthenticated hot path. The global
 *  method-classified tiers are deliberately absent: that middleware runs BEFORE auth
 *  (`app.use('*', rateLimiter)`), so it has no tenant to resolve a plan from. */
export const PLAN_RATE_LIMIT_TIERS = ['db', 'sdk', 'scripts'] as const
export type PlanRateLimitTier = (typeof PLAN_RATE_LIMIT_TIERS)[number]

export type PlanRateLimits = Partial<Record<PlanRateLimitTier, number>>

/**
 * Upper bound on a plan-configured ceiling.
 *
 * TWO jobs, and the second is why this number is not simply "very large":
 *
 *  1. A rate limit is a SAFETY VALVE, so an effectively-unbounded one is not a valid
 *     configuration — a mistyped extra digit would disable the tier silently and for
 *     good.
 *  2. It must stay BELOW the `/api/v1/*` flood valve (`RL_V1_PRE_MAX`, default 5000).
 *     A plan raised above that valve cannot take effect: the valve refuses first, sets
 *     no headers, and explains nothing — the operator sees the ceiling they configured
 *     on the price book and the customer sees 429s at a different number. That is the
 *     "tighter, less-informed layer wins" failure this whole change exists to remove,
 *     and it has now been reintroduced twice by picking these two numbers separately.
 *
 * So this is the CEILING OF LAST RESORT, deliberately below the valve's default. It is
 * a compile-time constant and therefore cannot follow a deployment that retunes the
 * valve — so the catalog route bounds a write by `min(this, RL_V1_PRE_MAX - 1)` read
 * live from the environment, and `apps/api/src/middleware/__tests__/rate-limit.test.ts`
 * pins the relationship against the REAL default rather than a copy of it.
 */
/**
 * Default for `RL_V1_PRE_MAX`, the `/api/v1/*` pre-auth flood valve.
 *
 * Lives HERE rather than beside the valve because THREE places must agree on it — the
 * limiter that enforces it, the catalog route that refuses a plan ceiling above it, and
 * the test that pins the relationship — and only `@papan/types` is importable by all
 * three (`apps/api` and `features/billing` may import types; types may import neither).
 *
 * The previous shape had each site hand-copy `5000`. That is not a guard: lowering the
 * real default left every copy, and every test, green while a plan raise silently lost
 * to a valve that now refused it first.
 */
export const V1_PRE_MAX_DEFAULT = 5_000

export const MAX_PLAN_RATE_LIMIT = 4_000

export function isPlanRateLimitTier(v: unknown): v is PlanRateLimitTier {
  return typeof v === 'string' && (PLAN_RATE_LIMIT_TIERS as readonly string[]).includes(v)
}

/**
 * The ONE implementation of "what ceiling does this plan set for this tier?".
 *
 * Takes the whole `features` blob rather than a pre-dug value, for the same reason
 * `planBlocksCreate` does: a caller that reaches in itself is a caller that can
 * re-introduce the trap at the call site.
 *
 * Returns `null` for absent / malformed / non-positive — never a number the caller
 * could mistake for a deliberate ceiling.
 */
export function resolvePlanRateLimit(
  features: { rateLimits?: unknown } | null | undefined,
  tier: PlanRateLimitTier,
): number | null {
  const raw = features?.rateLimits
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const v = (raw as Record<string, unknown>)[tier]
  if (typeof v !== 'number' || !Number.isFinite(v) || v <= 0) return null
  return Math.floor(v)
}

/** Normalise an operator-supplied blob for storage: drops unknown keys and any
 *  non-positive value, so a cleared field round-trips to "inherit the default"
 *  rather than persisting a 0 that would read as a deliberate block. */
export function normalisePlanRateLimits(input: unknown): PlanRateLimits | undefined {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return undefined
  const out: PlanRateLimits = {}
  for (const tier of PLAN_RATE_LIMIT_TIERS) {
    const v = (input as Record<string, unknown>)[tier]
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) out[tier] = Math.floor(v)
  }
  return Object.keys(out).length ? out : undefined
}
